import axios from 'axios';

//const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.memrearal.com/api';

// Helper function to get full URL for static files (avatars, media)
export const getStaticFileUrl = (path: string | null | undefined): string | null => {
	if (!path) return null;

	// If path already starts with http:// or https://, return as is
	if (path.startsWith('http://') || path.startsWith('https://')) {
		return path;
	}

	// If path starts with /api/, combine with API_BASE_URL
	if (path.startsWith('/api/')) {
		return `${API_BASE_URL}${path.replace('/api/', '/')}`;
	}

	// If path doesn't start with /api/, assume it's a relative path from /api/stalker/static
	if (path.startsWith('/')) {
		return `${API_BASE_URL}${path}`;
	}

	// Otherwise, treat as relative path from /api/stalker/static
	return `${API_BASE_URL}/stalker/static/${path}`;
};

// Helper function to get proxy URL for external Instagram media (to bypass CORS)
export const getProxyUrl = (url: string | null | undefined): string | null => {
	if (!url) return null;

	// If URL is from Instagram CDN, use proxy
	if (url.includes('instagram.com') || url.includes('cdninstagram.com') || url.includes('fbcdn.net')) {
		const encodedUrl = encodeURIComponent(url);
		return `${API_BASE_URL}/proxy?url=${encodedUrl}`;
	}

	// Otherwise, return as is
	return url;
};

// Axios instance oluştur
const api = axios.create({
	baseURL: API_BASE_URL,
	headers: {
		'Content-Type': 'application/json',
	},
});

// Request interceptor - token ekle
api.interceptors.request.use((config) => {
	const token = localStorage.getItem('token');
	if (token) {
		config.headers.Authorization = `Bearer ${token}`;
	}
	return config;
}, (error) => {
	return Promise.reject(error);
});

// Response interceptor - token süresi dolmuşsa logout
api.interceptors.response.use((response) => response, (error) => {
	if (error.response?.status === 401) {
		localStorage.removeItem('token');
		localStorage.removeItem('user');
		window.location.href = '/login';
	} else if (error.code === 'NETWORK_ERROR' || !error.response) {
		// Network hatası
		const event = new CustomEvent('networkError', {
			detail: { message: 'İnternet bağlantısı yok veya sunucuya ulaşılamıyor' }
		});
		window.dispatchEvent(event);
	} else if (error.response?.status >= 500) {
		// Sunucu hatası
		const event = new CustomEvent('serverError', {
			detail: { message: 'Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyin.' }
		});
		window.dispatchEvent(event);
	}
	return Promise.reject(error);
});

// Auth API
export const authAPI = {
	login: (credentials: { username: string; password: string }) => api.post('/auth/login', credentials),
	logout: () => api.post('/auth/logout'),
	checkAuth: () => api.get('/auth/check'),
	changePassword: (data: { oldPassword: string; newPassword: string }) => api.post('/auth/change-password', data),
};

// Password Manager API
export const passwordManagerAPI = {
	// Passwords
	passwords: {
		getAll: (params?: { search?: string; tag_ids?: string }) => api.get('/password-manager/passwords', { params }),
		getById: (id: number) => api.get(`/password-manager/passwords/${id}`),
		create: (data: any) => api.post('/password-manager/passwords', data),
		update: (id: number, data: any) => api.put(`/password-manager/passwords/${id}`, data),
		delete: (id: number) => api.delete(`/password-manager/passwords/${id}`),
		generatePassword: (params?: { length?: number; symbols?: boolean }) => api.get('/password-manager/passwords/generate', { params }),
	},
	// Tags
	tags: {
		getAll: () => api.get('/password-manager/tags'),
		create: (data: { name: string; color: string }) => api.post('/password-manager/tags', data),
		update: (id: number, data: { name: string; color: string }) => api.put(`/password-manager/tags/${id}`, data),
		delete: (id: number) => api.delete(`/password-manager/tags/${id}`),
	},
	// Export/Import
	export: {
		exportData: () => api.get('/password-manager/export'),
		importData: (data: any) => api.post('/password-manager/import', data),
	},
	// Logs
	logs: {
		getAll: (params?: { limit?: number }) => api.get('/password-manager/logs', { params }),
		getById: (id: number) => api.get(`/password-manager/logs/${id}`),
	},
};

// Stalker API
export const stalkerAPI = {
	// Dashboard
	dashboard: {
		get: () => api.get(`/stalker/dashboard`),
	},
	// Persons
	persons: {
		getAll: (params?: { person_pin?: string; person_name?: string; person_surname?: string }) => api.get('/stalker/persons', { params }),
		getById: (id: number) => api.get(`/stalker/persons/${id}`),
		getMedia: (id: number, params?: { page?: number; limit?: number }) => api.get(`/stalker/persons/${id}/media`, { params }),
		create: (data: any) => {
			if (data instanceof FormData) {
				return api.post('/stalker/persons', data, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				});
			}
			return api.post('/stalker/persons', data);
		},
		update: (id: number, data: any) => {
			if (data instanceof FormData) {
				return api.put(`/stalker/persons/${id}`, data, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				});
			}
			return api.put(`/stalker/persons/${id}`, data);
		},
		delete: (id: number) => api.delete(`/stalker/persons/${id}`),
	},
	// Logs
	logs: {
		getAll: (params?: { platform?: string; personId?: number; limit?: number }) => api.get('/stalker/logs', { params }),
		getById: (id: number) => api.get(`/stalker/logs/${id}`),
		getPast: (params: {
			accountId: number;
			platform: string;
			changeType?: string;
			startDate?: string;
			endDate?: string;
			limit?: number;
			order?: 'ASC' | 'DESC';
		}) => api.get('/stalker/logs/past', { params }),
	},
	// Numbers
	numbers: {
		getAll: () => api.get('/stalker/numbers'),
		getById: (id: number) => api.get(`/stalker/numbers/${id}`),
		create: (data: { pn_personId: number; pn_number: string; pn_status?: string }) => api.post('/stalker/numbers', data),
		update: (id: number, data: { pn_number?: string; pn_status?: string }) => api.put(`/stalker/numbers/${id}`, data),
		delete: (id: number) => api.delete(`/stalker/numbers/${id}`),
	},
	// Emails
	emails: {
		getAll: () => api.get('/stalker/emails'),
		getById: (id: number) => api.get(`/stalker/emails/${id}`),
		create: (data: { pm_personId: number; pm_mail: string; pm_status?: string }) => api.post('/stalker/emails', data),
		update: (id: number, data: { pm_mail?: string; pm_status?: string }) => api.put(`/stalker/emails/${id}`, data),
		delete: (id: number) => api.delete(`/stalker/emails/${id}`),
	},
	// Socials
	socials: {
		getAll: (params?: { platform?: string }) => api.get('/stalker/socials', { params }),
		getList: () => api.get('/stalker/socials/list'),
		getById: (platform: string, id: number) => api.get(`/stalker/socials/${platform}/${id}`),
		getMedia: (platform: string, id: number, params?: { page?: number; limit?: number }) => api.get(`/stalker/socials/${platform}/${id}/media`, { params }),
		create: (platform: string, data: any) => api.post(`/stalker/socials/${platform}`, data),
		update: (platform: string, id: number, data: any) => api.put(`/stalker/socials/${platform}/${id}`, data),
		delete: (platform: string, id: number) => api.delete(`/stalker/socials/${platform}/${id}`),
	},
	// Locations
	locations: {
		getAll: () => api.get('/stalker/locations'),
		getById: (id: number) => api.get(`/stalker/locations/${id}`),
		create: (data: { pl_personId: number; pl_locationTitle?: string; pl_location: string; pl_lat?: number; pl_lng?: number; pl_status?: string }) => api.post('/stalker/locations', data),
		update: (id: number, data: { pl_locationTitle?: string; pl_location?: string; pl_lat?: number; pl_lng?: number; pl_status?: string }) => api.put(`/stalker/locations/${id}`, data),
		delete: (id: number) => api.delete(`/stalker/locations/${id}`),
	},
	// Medias
	medias: {
		getAll: () => api.get('/stalker/medias'),
		getList: (params?: { limit?: number; offset?: number }) => api.get('/stalker/medias/list', { params }),
		getById: (id: number) => api.get(`/stalker/medias/${id}`),
		create: (data: FormData | { sm_personId: number; sm_platform: string; sm_mediaType?: string; sm_description?: string }) => {
			if (data instanceof FormData) {
				return api.post('/stalker/medias', data, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				});
			}
			return api.post('/stalker/medias', data);
		},
		update: (id: number, data: FormData | { sm_mediaType?: string; sm_description?: string }) => {
			if (data instanceof FormData) {
				return api.put(`/stalker/medias/${id}`, data, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				});
			}
			return api.put(`/stalker/medias/${id}`, data);
		},
		delete: (id: number) => api.delete(`/stalker/medias/${id}`),
	},
	// Settings
	settings: {
		getAll: () => api.get('/stalker/settings'),
		update: (settings: Record<string, string | null>) =>
			api.put('/stalker/settings', { settings }),
	},
};

// Notes API
export const notesAPI = {
	// Folders
	folders: {
		getAll: (params?: { parent_folder_id?: number | null }) => api.get('/notes/folders', { params }),
		getHierarchy: () => api.get('/notes/folders/hierarchy'),
		getById: (id: number) => api.get(`/notes/folders/${id}`),
		create: (data: { folder_name: string; parent_folder_id?: number | null; folder_order?: number }) => api.post('/notes/folders', data),
		update: (id: number, data: { folder_name?: string; parent_folder_id?: number | null; folder_order?: number }) => api.put(`/notes/folders/${id}`, data),
		delete: (id: number) => api.delete(`/notes/folders/${id}`),
	},
	// Notes
	notes: {
		getAll: (params?: { folder_id?: number; status?: string; is_archived?: boolean; is_pinned?: boolean; search?: string; tag_ids?: string; limit?: number; offset?: number }) => api.get('/notes/notes', { params }),
		getById: (id: number) => api.get(`/notes/notes/${id}`),
		verifyPassword: (id: number, password: string) => api.post(`/notes/notes/${id}/verify-password`, { password }),
		create: (data: any) => api.post('/notes/notes', data),
		update: (id: number, data: any) => api.put(`/notes/notes/${id}`, data),
		delete: (id: number, withoutTrash?: boolean) => api.delete(`/notes/notes/${id}`, { data: { withoutTrash } }),
		restore: (id: number) => api.post(`/notes/notes/${id}/restore`),
		togglePin: (id: number) => api.post(`/notes/notes/${id}/toggle-pin`),
		toggleArchive: (id: number) => api.post(`/notes/notes/${id}/toggle-archive`),
		search: (params?: { search: string; folder_id?: number; tag_ids?: string; limit?: number; offset?: number }) => api.get('/notes/notes/search', { params }),
		getHistory: (id: number, limit?: number) => api.get(`/notes/notes/${id}/history`, { params: { limit } }),
		restoreFromHistory: (history_id: number) => api.post(`/notes/notes/history/${history_id}/restore`),
	},
	// Sync
	sync: {
		getStatus: (device_id: string) => api.get('/notes/sync/status', { params: { device_id } }),
		getUnsynced: (device_id: string) => api.get('/notes/sync/unsynced', { params: { device_id } }),
		update: (note_id: number, device_id: string, version: number) => api.post('/notes/sync/update', { note_id, device_id, version }),
	},
	// Tags (uses same tags as password manager - shared tags)
	tags: {
		getAll: () => api.get('/password-manager/tags'),
		create: (data: { name: string; color: string }) => api.post('/password-manager/tags', data),
		update: (id: number, data: { name: string; color: string }) => api.put(`/password-manager/tags/${id}`, data),
		delete: (id: number) => api.delete(`/password-manager/tags/${id}`),
	},
	// Logs
	logs: {
		getAll: (params?: { limit?: number }) => api.get('/notes/logs', { params }),
		getById: (id: number) => api.get(`/notes/logs/${id}`),
	},
};

export default api;
